import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, MailOpen, Inbox } from "lucide-react";
import { BrandMark } from "@/components/Navbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const Admin = () => {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  const loadMessages = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/messages`, { withCredentials: true });
      setMessages(data.messages);
    } catch {
      toast.error("Could not load messages.");
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => {
        setUser(data);
        loadMessages();
      })
      .catch(() => setUser(null));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      loadMessages();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
    setMessages([]);
  };

  const markRead = async (id) => {
    await axios.patch(`${API}/admin/messages/${id}/read`, {}, { withCredentials: true });
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, read: true } : msg)));
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#F6F1EC] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#70435F]" size={28} />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="min-h-screen bg-[#F6F1EC] flex items-center justify-center px-5">
        <form
          onSubmit={login}
          className="w-full max-w-sm bg-white border border-[#70435F]/10 rounded-3xl p-8 shadow-card"
        >
          <div className="flex justify-center mb-6">
            <BrandMark size={48} />
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#70435F] text-center mb-1">Admin Access</h1>
          <p className="text-xs text-[#8A7E88] text-center mb-7">Website enquiries dashboard</p>
          <label htmlFor="admin-email" className="block text-xs uppercase tracking-[0.14em] font-semibold text-[#695E67] mb-2">
            Email
          </label>
          <input
            id="admin-email"
            data-testid="admin-email-input"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#FAF7F4] border border-[#E7DED4] rounded-xl px-4 py-3 text-sm mb-4 focus:outline-none focus:border-[#70435F]"
          />
          <label htmlFor="admin-password" className="block text-xs uppercase tracking-[0.14em] font-semibold text-[#695E67] mb-2">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-[#FAF7F4] border border-[#E7DED4] rounded-xl px-4 py-3 text-sm mb-5 focus:outline-none focus:border-[#70435F]"
          />
          {error && (
            <p data-testid="admin-login-error" className="text-xs text-red-500 mb-4">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#70435F] text-white py-3.5 rounded-full hover:bg-[#5A344C] transition-colors font-medium text-sm disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Sign In
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F6F1EC]">
      <header className="bg-white border-b border-[#E7DED4] px-5 sm:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BrandMark size={36} />
          <div>
            <p className="font-display text-lg font-semibold text-[#383237] leading-tight">Enquiries</p>
            <p className="text-xs text-[#8A7E88]">{user.email}</p>
          </div>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={logout}
          className="inline-flex items-center gap-2 text-sm text-[#70435F] border border-[#70435F]/25 rounded-full px-4 py-2 hover:bg-[#E8C2CB]/25 transition-colors"
        >
          <LogOut size={15} /> Sign Out
        </button>
      </header>
      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10">
        {messages.length === 0 ? (
          <div data-testid="admin-messages-empty" className="text-center py-24 text-[#8A7E88]">
            <Inbox size={36} className="mx-auto mb-4 text-[#E8C2CB]" />
            <p className="text-sm">No enquiries yet.</p>
          </div>
        ) : (
          <ul data-testid="admin-messages-list" className="space-y-4">
            {messages.map((m) => (
              <li
                key={m.id}
                data-testid={`admin-message-${m.id}`}
                className={`bg-white border rounded-2xl p-6 transition-colors ${
                  m.read ? "border-[#E7DED4]" : "border-[#E8C2CB] shadow-card"
                }`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <div>
                    <p className="text-sm font-semibold text-[#383237]">
                      {m.name}
                      {!m.read && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-[#70435F] bg-[#E8C2CB]/40 rounded-full px-2 py-0.5">
                          New
                        </span>
                      )}
                    </p>
                    <a href={`mailto:${m.email}`} className="text-xs text-[#70435F] hover:underline">
                      {m.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="text-xs text-[#8A7E88]">
                      {new Date(m.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                    {!m.read && (
                      <button
                        data-testid={`admin-mark-read-${m.id}`}
                        onClick={() => markRead(m.id)}
                        className="text-xs inline-flex items-center gap-1 text-[#70435F] hover:underline"
                      >
                        <MailOpen size={13} /> Mark read
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-xs uppercase tracking-[0.14em] font-semibold text-[#B89A69] mb-1.5">{m.subject}</p>
                <p className="text-sm text-[#695E67] leading-relaxed whitespace-pre-line">{m.message}</p>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
};

export default Admin;
